import React, {useCallback, useEffect, useRef} from 'react';

export enum SwipeDirection {
	Left = 'left',
	Right = 'right',
}

type Props = {
	onChange: (direction: SwipeDirection | null) => void;
	startRender: (direction: SwipeDirection) => void;
	reset: () => void;
	leftPage: boolean;
	rightPage: boolean;
	swipeAreaRef: React.RefObject<HTMLDivElement | null>;
	leftPageRef: React.RefObject<HTMLDivElement | null>;
	centerPageRef: React.RefObject<HTMLDivElement | null>;
	rightPageRef: React.RefObject<HTMLDivElement | null>;
};

/** страница уезжает за var(--duration-normal); столько ждём transitionend, прежде чем закончить сами */
const ANIMATION_TIMEOUT = 400;

function hasTouch(list: TouchList, touch: Touch) {
	for (let i = 0; i < list.length; i++) {
		if (list.item(i)?.identifier === touch.identifier) {
			return true;
		}
	}
	return false;
}

export function useTouch({
	onChange,
	reset,
	leftPage,
	rightPage,
	swipeAreaRef,
	leftPageRef,
	rightPageRef,
	centerPageRef,
	startRender,
}: Props) {
	const started = useRef(false);
	const detecting = useRef(false);
	const touch = useRef<Touch | undefined>(undefined);
	const x = useRef<number>(0);
	const y = useRef<number>(0);
	const delta = useRef<number>(0);
	const width = useRef<number>(0);
	const offset = useRef<number>(0);
	const leftSignal = useRef(false);
	const rightSignal = useRef(false);
	const startTime = useRef(0);

	const pages = useCallback(
		() => [leftPageRef.current, centerPageRef.current, rightPageRef.current],
		[leftPageRef, centerPageRef, rightPageRef],
	);

	const clear = useCallback(() => {
		started.current = false;
		detecting.current = false;
		touch.current = undefined;
		delta.current = 0;
		x.current = 0;
		y.current = 0;
		leftSignal.current = false;
		rightSignal.current = false;
		startTime.current = 0;
	}, []);

	function move(direction: SwipeDirection | null) {
		const pageWidth = width.current;
		const time = new Date().getTime() - startTime.current;
		const isSwipe = time < 200 || Math.abs(delta.current) > pageWidth / 3;

		if (
			!direction ||
			!isSwipe ||
			(direction === SwipeDirection.Left && !leftPage) ||
			(direction === SwipeDirection.Right && !rightPage)
		) {
			direction = null;
		}

		clear();
		for (const page of pages()) page?.classList.add('animating');

		if (!direction) {
			leftPageRef.current!.style.transform = `translate3d(${-pageWidth}px, 0, 0)`;
			centerPageRef.current!.style.transform = 'translate3d(0, 0, 0)';
			rightPageRef.current!.style.transform = `translate3d(${pageWidth}px, 0, 0)`;
		} else if (direction === SwipeDirection.Left) {
			leftPageRef.current!.style.transform = 'translate3d(0, 0, 0)';
			centerPageRef.current!.style.transform = `translate3d(${pageWidth}px, 0, 0)`;
			rightPageRef.current!.style.transform = `translate3d(${2 * pageWidth}px, 0, 0)`;
		} else {
			leftPageRef.current!.style.transform = `translate3d(${-2 * pageWidth}px, 0, 0)`;
			centerPageRef.current!.style.transform = `translate3d(${-pageWidth}px, 0, 0)`;
			rightPageRef.current!.style.transform = 'translate3d(0, 0, 0)';
		}

		let done = false;
		function end(e?: TransitionEvent) {
			// transitionend всплывает из содержимого страницы: свой ловим по цели и свойству
			if (e && (e.target !== centerPageRef.current || e.propertyName !== 'transform')) return;
			if (done) return;
			done = true;
			window.clearTimeout(timer);
			centerPageRef.current?.removeEventListener('transitionend', end);

			// сдвиг снимает Swipe, когда покажет новую страницу: иначе в кадре между
			// концом анимации и её отрисовкой обратно прыгает старая
			if (direction) onChange(direction);
			else reset();
		}

		const timer = window.setTimeout(end, ANIMATION_TIMEOUT);
		centerPageRef.current?.addEventListener('transitionend', end);
	}

	function draw(e: TouchEvent) {
		e.preventDefault();

		if (!touch.current || !hasTouch(e.changedTouches, touch.current)) {
			return;
		}

		const currentTouch = e.changedTouches[0];
		const newX = currentTouch.pageX;

		let currentDelta = x.current - newX;

		if ((currentDelta < 0 && !leftPage) || (currentDelta > 0 && !rightPage)) {
			currentDelta = currentDelta / 5;
		}

		// соседнюю страницу рисуем один раз за жест и ту, к которой ведёт палец
		if (currentDelta < 0 && !leftSignal.current) {
			leftSignal.current = true;
			startRender(SwipeDirection.Left);
		}
		if (currentDelta > 0 && !rightSignal.current) {
			rightSignal.current = true;
			startRender(SwipeDirection.Right);
		}

		delta.current = currentDelta;

		const pageWidth = width.current;

		leftPageRef.current!.style.transform = `translate3d(${-pageWidth - currentDelta}px, 0, 0)`;
		centerPageRef.current!.style.transform = `translate3d(${-currentDelta}px, 0, 0)`;
		rightPageRef.current!.style.transform = `translate3d(${pageWidth - currentDelta}px, 0, 0)`;

		for (const page of [leftPageRef.current, rightPageRef.current]) {
			if (page && page.scrollTop !== offset.current) page.scrollTop = offset.current;
		}
	}

	function detect(e: TouchEvent) {
		if (!touch.current || !hasTouch(e.changedTouches, touch.current)) {
			return;
		}

		const currentTouch = e.changedTouches[0];
		const newX = currentTouch.pageX;
		const newY = currentTouch.pageY;

		if (Math.abs(x.current - newX) >= Math.abs(y.current - newY)) {
			e.preventDefault();

			started.current = true;
			startTime.current = new Date().getTime();
			// ширину читаем один раз за жест: на каждом движении это лишний пересчёт вёрстки
			width.current = centerPageRef.current?.getBoundingClientRect().width || 0;
			offset.current = Math.max(0, -(swipeAreaRef.current?.getBoundingClientRect().top || 0));
			for (const page of [leftPageRef.current, rightPageRef.current]) {
				if (page) page.style.top = offset.current + 'px';
			}
			for (const page of pages()) page?.classList.remove('animating');
		}

		detecting.current = false;
	}

	const handleTouchStart = useCallback(
		(e: TouchEvent) => {
			if (e.touches.length !== 1 || started.current) {
				return;
			}

			detecting.current = true;

			touch.current = e.changedTouches[0];
			x.current = touch.current.pageX;
			y.current = touch.current.pageY;
		},
		[onChange],
	);

	const handleTouchMove = useCallback(
		(e: TouchEvent) => {
			if ((!started.current && !detecting.current) || e.touches.length > 1) {
				return;
			}

			if (detecting.current) {
				detect(e);
			}

			if (started.current) {
				draw(e);
			}
		},
		[onChange],
	);

	const handleTouchEnd = useCallback(
		(e: TouchEvent) => {
			if (!touch.current || !hasTouch(e.changedTouches, touch.current) || !started.current) {
				return;
			}
			e.preventDefault();

			// палец коснулся и не поехал: жест просто заканчиваем, иначе следующий не начнётся
			if (!delta.current) {
				clear();
				return;
			}

			move(delta.current < 0 ? SwipeDirection.Left : SwipeDirection.Right);
		},
		[onChange],
	);

	const handleTouchCancel = useCallback(() => {
		if (!started.current) {
			clear();
			return;
		}
		move(null);
	}, [onChange]);

	useEffect(() => {
		swipeAreaRef.current!.addEventListener('touchstart', handleTouchStart);
		swipeAreaRef.current!.addEventListener('touchmove', handleTouchMove);
		swipeAreaRef.current!.addEventListener('touchend', handleTouchEnd);
		swipeAreaRef.current!.addEventListener('touchcancel', handleTouchCancel);

		return () => {
			swipeAreaRef.current?.removeEventListener('touchstart', handleTouchStart);
			swipeAreaRef.current?.removeEventListener('touchmove', handleTouchMove);
			swipeAreaRef.current?.removeEventListener('touchend', handleTouchEnd);
			swipeAreaRef.current?.removeEventListener('touchcancel', handleTouchCancel);
		};
	}, [onChange]);

	return {};
}
